import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import {
    OffstreetEventProductOption,
    useOffstreetEventsOutlet,
} from './OperatorOffstreetEvents.tsx';
import {
    RequestStatus,
    ServerRequestState,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import { ParkingPortalLayoutWithHeader } from '../mobile/layout/ParkingPortalLayoutWithHeader.tsx';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { Box, Button, MenuItem } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import {
    mandatoryFieldErrorMessage,
    tooManyCharsMessage,
} from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { SubmitButton } from '../ui/buttons/SubmitButton.tsx';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';

type CreateOffstreetEventPayload = {
    contractTemplateId: number;
    name: string;
    validFrom: DateTime;
    validTo: DateTime;
};

export function OperatorOffstreetEventsAdd() {
    const { productOptions, refetchEvents } = useOffstreetEventsOutlet();
    const navigate = useNavigate();
    const [createOffstreetEventState, createOffstreetEvent] = useServerWrite<
        CreateOffstreetEventPayload,
        unknown,
        OffstreetEventErrorResponse
    >(() => ({
        url: `/ui-api/operator-account/offstreet-events/add`,
    }));
    useServerSuccessEffect(createOffstreetEventState, () => {
        refetchEvents();
        navigate('..');
    });

    return (
        <OperatorRoutedModal
            variant={ModalVariant.TINY}
            backUrl={'..'}
            style={{ maxHeight: undefined }}
            render={() => (
                <ParkingPortalLayoutWithHeader
                    title={
                        <Localized
                            de="Neue Veranstaltung"
                            fr="Nouvelle manifestation"
                            it="Nuovo evento"
                            en="New event"
                        />
                    }
                    backTo={'..'}
                >
                    <OperatorOffstreetEventsCreateUpdateForm
                        submitRequestState={createOffstreetEventState}
                        onSubmit={({
                            contractTemplateId,
                            name,
                            validFrom,
                            validTo,
                        }) => {
                            if (
                                contractTemplateId == null ||
                                validFrom == null ||
                                validTo == null
                            ) {
                                throw new Error(
                                    'Required form fields are missing on submit',
                                );
                            }
                            createOffstreetEvent({
                                contractTemplateId,
                                name,
                                validFrom,
                                validTo,
                            });
                        }}
                        productOptions={productOptions}
                    />
                </ParkingPortalLayoutWithHeader>
            )}
        />
    );
}

const enum OffstreetEventFields {
    contractTemplateId = 'contractTemplateId',
    name = 'name',
    validFrom = 'validFrom',
    validTo = 'validTo',
}

interface OffstreetEventFieldValues {
    [OffstreetEventFields.contractTemplateId]: number | null;
    [OffstreetEventFields.name]: string;
    [OffstreetEventFields.validFrom]: DateTime | null;
    [OffstreetEventFields.validTo]: DateTime | null;
}

export function OperatorOffstreetEventsCreateUpdateForm({
    submitRequestState,
    onSubmit,
    productOptions,
    defaultValues,
}: {
    submitRequestState: ServerRequestState<
        unknown,
        OffstreetEventErrorResponse
    >;
    onSubmit: (formFieldValues: OffstreetEventFieldValues) => void;
    productOptions: OffstreetEventProductOption[];
    defaultValues?: OffstreetEventFieldValues;
}) {
    const { language } = useLanguage();
    const navigate = useNavigate();

    const { control, handleSubmit, watch } = useForm<OffstreetEventFieldValues>(
        {
            defaultValues: defaultValues
                ? defaultValues
                : {
                      [OffstreetEventFields.contractTemplateId]:
                          productOptions.length == 1
                              ? productOptions[0].contractTemplateId
                              : null,
                      [OffstreetEventFields.name]: '',
                      [OffstreetEventFields.validFrom]: null,
                      [OffstreetEventFields.validTo]: null,
                  },
            mode: 'onBlur',
        },
    );
    // eslint-disable-next-line react-hooks/incompatible-library
    const validFrom = watch(OffstreetEventFields.validFrom);

    const validTo = watch(OffstreetEventFields.validTo);

    const maxLengthDescription = 50;
    const descriptionTooLong = useTranslation(
        tooManyCharsMessage(maxLengthDescription),
    );
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    const savePending = submitRequestState.status == RequestStatus.PENDING;
    return (
        <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
                padding: '25px',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
            }}
        >
            <OperatorOffstreetEventErrorModal
                submitRequestState={submitRequestState}
            />

            <ReactHookFormTextField
                readonly={productOptions.length < 2}
                label={<Localized de="Typ" fr="Type" it="Tipo" en="Type" />}
                name={OffstreetEventFields.contractTemplateId}
                control={control}
                select
                rules={{
                    required: {
                        value: true,
                        message: mandatoryFieldMessage,
                    },
                }}
            >
                {productOptions
                    .sort((a, b) =>
                        a.productName[language].localeCompare(
                            b.productName[language],
                        ),
                    )
                    .map(productTemplate => (
                        <MenuItem
                            key={productTemplate.contractTemplateId}
                            value={productTemplate.contractTemplateId}
                        >
                            <Localized {...productTemplate.productName} />
                        </MenuItem>
                    ))}
            </ReactHookFormTextField>
            <ReactHookFormTextField
                label={
                    <Localized
                        de="Bezeichnung"
                        fr="Désignation"
                        it="Descrizione"
                        en="Description"
                    />
                }
                name={OffstreetEventFields.name}
                control={control}
                rules={{
                    required: {
                        value: true,
                        message: mandatoryFieldMessage,
                    },
                    maxLength: {
                        value: maxLengthDescription,
                        message: descriptionTooLong,
                    },
                    minLength: {
                        value: 1,
                        message: mandatoryFieldMessage,
                    },
                }}
            />
            <Box sx={{ width: '50%' }}>
                <Controller
                    name={OffstreetEventFields.validFrom}
                    control={control}
                    render={({ field, fieldState }) => (
                        <DesktopDatePicker
                            readOnly={
                                defaultValues?.validFrom
                                    ? defaultValues.validFrom < DateTime.now()
                                    : false
                            }
                            format="dd.MM.yyyy"
                            label={
                                <Localized
                                    de="Beginn"
                                    fr="Début"
                                    it="Inizio"
                                    en="Start"
                                />
                            }
                            value={field.value ?? undefined}
                            onChange={field.onChange}
                            minDate={
                                validTo
                                    ? DateTime.max(
                                          DateTime.now().plus({
                                              days: 1,
                                          }),
                                          validTo.minus({
                                              months: 1,
                                          }),
                                      )
                                    : DateTime.now().plus({
                                          days: 1,
                                      })
                            }
                            maxDate={validTo ?? undefined}
                            slotProps={{
                                textField: {
                                    error: !!fieldState.error,
                                    helperText: fieldState.error?.message,
                                    fullWidth: true,
                                },
                            }}
                        />
                    )}
                    rules={{
                        required: {
                            value: true,
                            message: mandatoryFieldMessage,
                        },
                    }}
                />
            </Box>
            <Box sx={{ width: '50%' }}>
                <Controller
                    name={OffstreetEventFields.validTo}
                    control={control}
                    render={({ field, fieldState }) => (
                        <DesktopDatePicker
                            format="dd.MM.yyyy"
                            label={
                                <Localized
                                    de="Ende"
                                    fr="Fin"
                                    it="Fine"
                                    en="End"
                                />
                            }
                            value={field.value ?? undefined}
                            onChange={field.onChange}
                            minDate={
                                validFrom
                                    ? DateTime.max(
                                          DateTime.now().plus({
                                              days: 1,
                                          }),
                                          validFrom,
                                      )
                                    : DateTime.now().plus({
                                          days: 1,
                                      })
                            }
                            maxDate={
                                validFrom
                                    ? validFrom.plus({
                                          months: 1,
                                      })
                                    : undefined
                            }
                            slotProps={{
                                textField: {
                                    error: !!fieldState.error,
                                    helperText: fieldState.error?.message,
                                    fullWidth: true,
                                },
                            }}
                        />
                    )}
                    rules={{
                        required: {
                            value: true,
                            message: mandatoryFieldMessage,
                        },
                    }}
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    paddingTop: '8px',
                }}
            >
                <Button
                    onClick={() => navigate('..')}
                    variant="outlined"
                    disabled={savePending}
                >
                    <Localized
                        de="Abbrechen"
                        fr="Annuler"
                        it="Annulla"
                        en="Cancel"
                    />
                </Button>
                <SubmitButton
                    sx={{ marginLeft: '5px' }}
                    loading={savePending}
                    label={
                        <Localized
                            de="Speichern"
                            fr="Sauvegarder"
                            it="Salva"
                            en="Save"
                        />
                    }
                />
            </Box>
        </Box>
    );
}

const enum OffstreetEventError {
    TIME_ERROR = 'TIME_ERROR',
}

export type OffstreetEventErrorResponse = {
    error: OffstreetEventError;
};

export function OperatorOffstreetEventErrorModal({
    submitRequestState,
}: {
    submitRequestState: ServerRequestState<
        unknown,
        OffstreetEventErrorResponse
    >;
}) {
    if (submitRequestState.status != RequestStatus.ERROR) {
        return null;
    }
    switch (submitRequestState.data?.error ?? null) {
        case OffstreetEventError.TIME_ERROR:
            return (
                <Alert severity="error" sx={{ margin: '0' }}>
                    <Localized
                        de="Die Veranstaltung darf frühestens morgen starten und darf maximal einen Monat dauern."
                        fr="L'événement ne peut commencer qu'à partir de demain et ne peut durer plus d'un mois."
                        it="L'evento può iniziare al più presto domani e può durare al massimo un mese."
                        en="Please choose a start date from tomorrow onwards. The event can last up to one month."
                    />
                </Alert>
            );
        case null:
            return (
                <Alert severity="error" sx={{ margin: '0' }}>
                    <Localized
                        de="Es ist ein Fehler aufgetreten."
                        fr="Une erreur est survenue."
                        it="Si è verificato un errore."
                        en="An error has occurred."
                    />
                </Alert>
            );
    }
}
